import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Download, FileCheck2, Plus, Search } from 'lucide-react';
import api from '../../services/api';
import { errorMessage, useFetch } from '../../services/operatorApi';
import { Button, Card, DocumentStatusBadge, Empty, ErrorBox, Modal, PageHeader, SignerStatusBadge, Spinner, Table, Td, dateTime, inputClass } from '../../components/ui';

interface Doc {
  id: string;
  title: string;
  status: string;
  external_ref: string | null;
  created_at: string;
  completed_at?: string | null;
}
interface Signer {
  id: string;
  full_name: string;
  email: string;
  status: string;
  signed_at: string | null;
  decline_reason: string | null;
}
interface DocDetail extends Doc {
  signers: Signer[];
  recent_events: { type: string; occurred_at: string }[];
}

const STATUSES: [string, string][] = [
  ['', 'Tous les statuts'],
  ['in_progress', 'En cours'],
  ['sent', 'Envoyés'],
  ['completed', 'Signés'],
  ['draft', 'Brouillons'],
  ['voided', 'Annulés'],
];

export default function ClientDocuments() {
  const sent = (useLocation().state as { sent?: string } | null)?.sent;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, error, loading, reload } = useFetch(
    async () => (await api.get<{ documents: Doc[] }>('/documents', { params: { limit: 100, status: status || undefined } })).data.documents ?? [],
    [status]
  );

  const filtered = (data ?? []).filter((d) => `${d.title} ${d.external_ref ?? ''}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Documents"
        subtitle="Vos documents envoyés à la signature, leurs signataires et leurs preuves"
        actions={<Link to="/documents/new" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500"><Plus className="h-4 w-4" />Nouveau document</Link>}
      />
      {sent && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          « {sent} » est envoyé : chaque signataire reçoit son lien par e-mail. Vous suivez ici l&apos;avancement.
        </div>
      )}
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className={`${inputClass} pl-9`} placeholder="Titre ou référence…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className={`${inputClass} !w-auto`} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox message={error} onRetry={reload} />
        ) : filtered.length === 0 ? (
          search || status ? (
            <Empty>Aucun document ne correspond.</Empty>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <img src="/img/empty.webp" alt="" className="h-44 w-44 rounded-3xl object-contain" />
              <p className="mt-2 text-lg font-semibold text-slate-900">Votre premier document vous attend</p>
              <p className="mt-1 max-w-sm text-sm text-slate-500">Déposez un PDF, ajoutez vos signataires : l'invitation part en un clic.</p>
              <Link to="/documents/new" className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500"><Plus className="h-4 w-4" />Créer un document</Link>
            </div>
          )
        ) : (
          <Table head={['Document', 'Statut', 'Créé le', 'Signé le']}>
            {filtered.map((doc) => (
              <tr key={doc.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(doc.id)}>
                <Td>
                  <span className="font-medium text-slate-900">{doc.title}</span>
                  {doc.external_ref && <div className="text-xs text-slate-500">{doc.external_ref}</div>}
                </Td>
                <Td><DocumentStatusBadge status={doc.status} /></Td>
                <Td className="whitespace-nowrap">{dateTime(doc.created_at)}</Td>
                <Td className="whitespace-nowrap">{dateTime(doc.completed_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {openId && <DocumentModal id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </div>
  );
}

function DocumentModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data, error, loading, reload } = useFetch(async () => (await api.get<DocDetail>(`/documents/${id}`)).data, [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function open(kind: 'pdf' | 'evidence') {
    setBusy(kind);
    setFailure(null);
    try {
      const response = await api.get<{ download_url: string }>(kind === 'pdf' ? `/documents/${id}/download` : `/documents/${id}/evidence`, kind === 'pdf' ? { params: { format: 'pdf' } } : undefined);
      window.open(response.data.download_url, '_blank', 'noopener');
    } catch (e) {
      setFailure(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function cancel() {
    if (!confirm('Annuler ce document ? Les signataires ne pourront plus le signer.')) return;
    setBusy('void');
    setFailure(null);
    try {
      await api.delete(`/documents/${id}`, { data: { reason: 'Annulé depuis la console client' } });
      await reload();
      onChanged();
    } catch (e) {
      setFailure(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal title={data?.title ?? 'Document'} onClose={onClose} wide>
      {loading && !data ? (
        <Spinner />
      ) : error || !data ? (
        <ErrorBox message={error ?? 'Document introuvable'} onRetry={reload} />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <DocumentStatusBadge status={data.status} />
            <span className="text-sm text-slate-500">Créé le {dateTime(data.created_at)}</span>
            {data.external_ref && <span className="text-sm text-slate-500">Réf. {data.external_ref}</span>}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Signataires</h3>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {data.signers.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{s.full_name}</p>
                    <p className="text-xs text-slate-500">{s.email}{s.signed_at ? ` · signé le ${dateTime(s.signed_at)}` : ''}{s.decline_reason ? ` · motif : ${s.decline_reason}` : ''}</p>
                  </div>
                  <SignerStatusBadge status={s.status} />
                </li>
              ))}
            </ul>
          </div>

          {failure && <ErrorBox message={failure} />}
          <div className="flex flex-wrap gap-2">
            {data.status === 'completed' && (
              <>
                <Button onClick={() => open('pdf')} loading={busy === 'pdf'}><Download className="h-4 w-4" />PDF signé</Button>
                <Button variant="secondary" onClick={() => open('evidence')} loading={busy === 'evidence'}><FileCheck2 className="h-4 w-4" />Dossier de preuve</Button>
              </>
            )}
            {!['completed', 'voided'].includes(data.status) && <Button variant="danger" onClick={cancel} loading={busy === 'void'}>Annuler le document</Button>}
          </div>
          {data.status === 'completed' && <p className="text-xs text-slate-500">Le dossier de preuve contient le journal d&apos;audit, les certificats et les horodatages. Les liens de téléchargement expirent après 15 minutes.</p>}
        </div>
      )}
    </Modal>
  );
}
